import React, { useEffect, useRef } from "react";

/**
 * "Continue with Google" button — kicks off Google OAuth.
 *
 *  WEB FLOW (browser, PWA):
 *    window.location → accounts.google.com → bidorah.com/auth/google
 *
 *  NATIVE FLOW (Capacitor APK):
 *    @capacitor/browser opens accounts.google.com in a Chrome Custom Tab
 *    (separate from the WebView, so Google does NOT throw 403
 *    disallowed_useragent). After auth Google redirects to a CUSTOM
 *    URL SCHEME — com.bidorah.app://auth/google?code=... — which the
 *    AndroidManifest intent-filter catches and bounces back into the
 *    APK. The App component (see App.js appUrlOpen listener) extracts
 *    the code and POSTs it to /api/auth/google to mint a session.
 *
 *  Why this is the only path: Google blocks OAuth in raw WebViews
 *  (policy "Use secure browsers", 2021). Custom Tabs are Chrome under
 *  the hood, so Google allows them.
 *
 *  CANONICAL HOST NORMALIZATION (Feb 2026):
 *    Users who hit bidorah.com (apex) instead of www.bidorah.com used to
 *    get a Google 400 `redirect_uri_mismatch` because the redirect_uri
 *    is built from window.location.origin. We now bounce the browser to
 *    the canonical www host BEFORE starting OAuth so redirect_uri is
 *    always https://www.bidorah.com/auth/google — no matter what URL
 *    the user typed. Only affects browser flow, not the native APK
 *    (Capacitor already runs on a fixed WebView origin).
 *
 *  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 *  URLS, THIS BREAKS THE AUTH.
 */

// Canonical public web host for OAuth. Mirrors `_CANONICAL_PUBLIC_HOST`
// in /app/backend/routes_auth.py. If you change one, change both.
// NOTE (2026-02-25): Emergent hosting serves the apex (bidorah.com) and
// 308-redirects www.bidorah.com → bidorah.com. If we ever flipped this
// to www we'd create a redirect ping-pong (apex → www → 308 → apex).
const CANONICAL_WEB_ORIGIN = "https://bidorah.com";

// Common in-app browsers that Google's OAuth policy blocks with 403
// `disallowed_useragent`. When we detect one, we redirect the user out
// to their system browser instead of pointing them at Google inside a
// dead-end WebView.
const IN_APP_BROWSER_RE = /(FBAN|FBAV|FB_IAB|Instagram|Line|MicroMessenger|MiuiBrowser|TikTok|BytedanceWebview|Twitter|LinkedInApp|Snapchat|Pinterest)/i;

export default function GoogleSignInButton({ label = "Continue with Google", testid }) {
  const autoTriggered = useRef(false);
  const start = async () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      // eslint-disable-next-line no-console
      console.error("Google sign-in is not configured (REACT_APP_GOOGLE_CLIENT_ID missing).");
      try {
        const { toast } = await import("sonner");
        toast.error("Google sign-in is not configured on this build.");
      } catch (_) { alert("Google sign-in is not configured on this build."); }
      return;
    }
    // Detect native at runtime — keep the import dynamic so the web
    // bundle doesn't pull Capacitor on bidorah.com browser visits.
    let isNative = false;
    try {
      const { Capacitor } = await import("@capacitor/core");
      isNative = Capacitor.isNativePlatform();
    } catch (_) { isNative = false; }

    // ── Canonical-host bounce (web only) ─────────────────────────────
    // If the user hit bidorah.com/... (apex) or any other bidorah.com
    // variant that isn't the canonical `www.bidorah.com`, redirect the
    // browser to the canonical host FIRST — carrying an auto-start
    // flag — so redirect_uri sent to Google is always the same string.
    // Google requires an EXACT match against the URIs registered in
    // Cloud Console; www vs apex is the #1 source of production OAuth
    // failures. Native (Capacitor) already runs on a fixed WebView
    // origin, so it's exempt.
    if (!isNative && typeof window !== "undefined") {
      const host = window.location.hostname.toLowerCase();
      const isBidorah = host.endsWith("bidorah.com");
      // ── Preview / dev / non-production host guard ────────────────
      // Google Cloud Console only allows OAuth from redirect URIs on
      // bidorah.com. Preview pods (`*.preview.emergentagent.com`), any
      // localhost / staging domain, or any host we haven't explicitly
      // whitelisted will get Google's generic 403 "That's an error"
      // page — which is confusing UX. Short-circuit here with a clear
      // toast so testers know to use email/password on preview.
      if (!isBidorah) {
        try {
          const { toast } = await import("sonner");
          toast.info(
            "Google Sign-In only works on production (bidorah.com). Use email + password on preview.",
            { duration: 6000 }
          );
        } catch (_) {
          alert("Google Sign-In only works on production. Use email + password on preview.");
        }
        return;
      }
      // Canonical is the apex (bidorah.com). Emergent 308-redirects
      // www.bidorah.com → bidorah.com, so we bounce www (and any other
      // subdomain) to the apex to avoid a redirect loop mid-OAuth.
      const isCanonical = host === "bidorah.com";
      if (isBidorah && !isCanonical) {
        const target = new URL(CANONICAL_WEB_ORIGIN + window.location.pathname + window.location.search);
        target.searchParams.set("_googleAuto", "1");
        window.location.replace(target.toString());
        return;
      }
    }

    // ── In-app browser detection ────────────────────────────────────
    // Instagram / Facebook / TikTok / etc. open links in their own
    // WebView. Google returns 403 `disallowed_useragent` on those.
    // We can't sign the user in from inside a dead-end WebView, so we
    // warn them and try to open in the OS default browser.
    if (!isNative && typeof window !== "undefined") {
      const ua = navigator.userAgent || "";
      if (IN_APP_BROWSER_RE.test(ua)) {
        try {
          const { toast } = await import("sonner");
          toast.error(
            "Google blocks sign-in inside in-app browsers. Tap the ••• menu → \"Open in Chrome/Safari\" and try again.",
            { duration: 8000 }
          );
        } catch (_) {
          alert("Google blocks sign-in inside in-app browsers. Please open in Chrome or Safari.");
        }
        return;
      }
    }

    const redirectUri = `${window.location.origin}/auth/google`;
    // We tag the OAuth `state` parameter with a "native:" prefix when
    // running inside Capacitor. Google still redirects to the same
    // https://bidorah.com/auth/google URL (which is what they accept —
    // custom URI schemes are rejected on Web-application OAuth clients).
    // When the Chrome Custom Tab lands on bidorah.com/auth/google, the
    // AuthCallback page sees the "native:" prefix and bounces to the
    // custom URL scheme com.bidorah.app://auth/google?... — Android
    // routes that back into the APK, where AuthCallback runs again
    // inside the WebView and completes the code exchange with cookies
    // set in the APK's origin.
    const random = Math.random().toString(36).slice(2) + Date.now().toString(36);
    const state = isNative ? `native:${random}` : random;
    try { sessionStorage.setItem("bidorah.google.oauth.state", state); } catch { /* ignore */ }
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      include_granted_scopes: "true",
      prompt: "select_account",
      state,
    });
    const url = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;

    if (isNative) {
      // Open in Chrome Custom Tab. Google sees Chrome's UA → allowed.
      // If the @capacitor/browser plugin isn't registered in this
      // build of the APK (cap sync wasn't run before assemble), the
      // dynamic import or Browser.open call will throw. We catch it
      // and fall back to opening Google in the system default
      // browser via Android's intent system, so the seller is never
      // left staring at a dead button.
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url, presentationStyle: "popover" });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[google-signin] Browser.open failed — falling back to window.open", e);
        try {
          const { toast } = await import("sonner");
          toast.warning("Opening Google in your default browser…");
        } catch (_) {}
        // window.open with _system target on Capacitor falls back to
        // an external Intent. If even that fails, last-ditch is to
        // set window.location.href — Google will reject the WebView
        // UA but at least the user sees something instead of nothing.
        try {
          window.open(url, "_system");
        } catch (_) {
          window.location.href = url;
        }
      }
    } else {
      window.location.href = url;
    }
  };

  // Auto-launch OAuth once when the page mounts with `?_googleAuto=1`
  // in the URL — set by the canonical-host bounce above. Strip the
  // flag first so a browser refresh doesn't retrigger the flow.
  useEffect(() => {
    if (autoTriggered.current) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("_googleAuto") !== "1") return;
    autoTriggered.current = true;
    params.delete("_googleAuto");
    const cleaned = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (cleaned ? `?${cleaned}` : "") + window.location.hash
    );
    // Defer one tick so any parent-level auth checks settle first.
    const t = setTimeout(() => { start(); }, 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <button
      type="button"
      onClick={start}
      className="w-full h-11 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-800 font-semibold text-sm flex items-center justify-center gap-3 transition-colors shadow-sm"
      data-testid={testid || "google-signin-btn"}
      aria-label={label}
    >
      <GoogleGlyph />
      <span>{label}</span>
    </button>
  );
}

// Multi-color Google "G" mark — inline SVG so we don't pull a font/icon
// pack just for one logo. Sizes proportionally with text.
function GoogleGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
